import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import api from '../../services/api';

const Icons = {
  Play: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  ),
  Stop: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="5" width="14" height="14" rx="2" />
    </svg>
  ),
  Refresh: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 16h6v6" />
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 8h-6V2" />
    </svg>
  ),
  Phone: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 3.08 5.18 2 2 0 0 1 5.06 3h3a2 2 0 0 1 2 1.72c.12.9.32 1.77.59 2.61a2 2 0 0 1-.45 2.11L9 10.64a16 16 0 0 0 4.36 4.36l1.2-1.2a2 2 0 0 1 2.11-.45c.84.27 1.71.47 2.61.59A2 2 0 0 1 22 16.92Z" />
    </svg>
  ),
  Store: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 10h18" />
      <path d="M5 10v10h14V10" />
      <path d="M7 10V5h10v5" />
    </svg>
  ),
  Qr: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h2v2h-2zM18 14h3v3M14 18h3v3M19 19h2v2" />
    </svg>
  ),
  Check: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  ),
  Alert: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v5" />
      <path d="M12 16h.01" />
    </svg>
  ),
  Trash: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6v14H5V6" />
    </svg>
  ),
  Close: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  ),
};

const SELECT_ALL = 'TODAS';

function safeParseUser() {
  const keys = ['vizumUser', 'lumixUser', 'appUser', 'user', 'authUser'];

  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key);
      if (raw) return JSON.parse(raw) || {};
    } catch {
      // ignora localStorage inválido
    }
  }

  return {};
}

function normalizarId(valor) {
  if (!valor) return '';
  if (typeof valor === 'object') return String(valor._id || valor.id || valor.toString?.() || '');
  return String(valor);
}

function isMongoId(valor) {
  return /^[0-9a-fA-F]{24}$/.test(String(valor || ''));
}

function getEmpresaIdFromUser(user = {}) {
  return normalizarId(user?.empresaId?._id || user?.empresaId || user?.empresa?._id || user?.empresa?.id || '');
}

function getSocketUrl() {
  const base = api.defaults.baseURL || window.location.origin;

  try {
    const url = new URL(base, window.location.origin);
    return `${url.protocol}//${url.host}`;
  } catch {
    return window.location.origin;
  }
}

function resolverArrayApi(data, keys = []) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
}

function montarErroApi(error) {
  if (!navigator.onLine) return 'Você está sem internet. Verifique a conexão e tente novamente.';

  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    'Falha ao comunicar com a API.'
  );
}

function getCanalId(canal = {}) {
  return normalizarId(canal._id || canal.id || canal.canalWhatsappId || canal.canalId);
}

function getCanalEmpresaId(canal = {}, fallback = '') {
  return normalizarId(canal?.empresaId?._id || canal?.empresaId || canal?.empresa?._id || canal?.empresa?.id || fallback);
}

function getUnidadeId(canal = {}) {
  return normalizarId(
    canal?.unidadeId?._id ||
      canal?.unidadeId ||
      canal?.unidade?._id ||
      canal?.unidade?.id ||
      canal?.empresaId?._id ||
      canal?.empresaId ||
      ''
  );
}

function getCanalNome(canal = {}) {
  return canal.nome || canal.label || canal.apelido || canal.numero || canal.sessionName || `Canal ${getCanalId(canal).slice(-6)}`;
}

function getUnidadeNome(canal = {}) {
  return (
    canal?.unidade?.nome ||
    canal?.unidadeId?.nome ||
    canal?.unidadeNome ||
    canal?.loja ||
    canal?.codigoLoja ||
    canal?.empresaId?.unidade ||
    canal?.empresaId?.nome ||
    'Unidade'
  );
}

function getCanalCodigoLoja(canal = {}) {
  return String(
    canal?.codigoLoja ||
      canal?.loja ||
      canal?.unidade?.codigoLoja ||
      canal?.unidadeId?.codigoLoja ||
      canal?.empresaId?.codigoLoja ||
      ''
  ).trim();
}

function getCanalStatus(canal = {}) {
  const status = String(canal?.runtime?.status || canal?.status || canal?.state || 'DISCONNECTED').toUpperCase();

  if (['INCHAT', 'CHATSAVAILABLE', 'ISLOGGED'].includes(status)) return 'CONNECTED';
  if (['QR', 'QR_CODE'].includes(status)) return 'QRCODE';
  if (['OPENING', 'LOADING'].includes(status)) return 'INITIALIZING';

  return status;
}

function isCanalConnected(canal = {}) {
  return canal?.runtime?.connected === true || canal?.connected === true || getCanalStatus(canal) === 'CONNECTED';
}

function getQrCode(canal = {}) {
  return canal?.runtime?.qrCode || canal?.qrCode || null;
}

function normalizarQrCode(qr = '') {
  if (!qr) return '';
  const value = String(qr);
  if (value.startsWith('data:image')) return value;
  if (value.startsWith('http')) return value;
  return `data:image/png;base64,${value}`;
}

function statusLabel(status = '') {
  switch (String(status).toUpperCase()) {
    case 'CONNECTED':
      return 'Conectado';
    case 'QRCODE':
      return 'Aguardando QR';
    case 'INITIALIZING':
      return 'Inicializando';
    case 'DISCONNECTING':
      return 'Desconectando';
    case 'ERROR':
      return 'Erro';
    default:
      return 'Desconectado';
  }
}

function limparMensagem(msg = '') {
  return String(msg || '').replace(/^[^\wÀ-ÿ[(]+/, '').replace(/\s+/g, ' ').trim();
}

function tipoLog(log = {}) {
  const tipo = String(log.tipo || log.type || '').toLowerCase();
  const msg = String(log.msg || log.message || '').toLowerCase();

  if (tipo.includes('erro') || tipo.includes('falha')) return 'erro';
  if (tipo.includes('sucesso') || tipo.includes('enviado') || msg.includes('mensagem enviada para')) return 'enviado';
  if (tipo.includes('ignorado') || msg.includes('ignorado') || msg.includes('fora do gatilho')) return 'ignorado';
  if (tipo.includes('analis') || msg.includes('desde a entrega') || msg.includes('analisando')) return 'analisado';
  if (tipo.includes('finalizado') || msg.includes('finalizado') || msg.includes('concluído')) return 'finalizado';

  return 'info';
}

function calcularStats(logs = []) {
  return logs.reduce(
    (acc, log) => {
      const tipo = tipoLog(log);
      if (tipo === 'analisado') acc.analisados += 1;
      if (tipo === 'enviado') acc.enviados += 1;
      if (tipo === 'ignorado') acc.ignorados += 1;
      if (tipo === 'erro') acc.erros += 1;
      return acc;
    },
    { analisados: 0, enviados: 0, ignorados: 0, erros: 0 }
  );
}

function montarPayloadReguaCanal(canal = {}, empresaIdAtual = '') {
  const empresaId = getCanalEmpresaId(canal, empresaIdAtual);
  const unidadeId = getUnidadeId(canal);
  const canalWhatsappId = getCanalId(canal);
  const lojaSelecionada = getCanalCodigoLoja(canal) || getUnidadeNome(canal);

  return {
    empresaId,
    unidadeId,
    canalWhatsappId,
    loja: lojaSelecionada,
    lojaSelecionada,
    origem: 'MANUAL',
    tipoExecucao: 'regua_pos_venda',
    multicanal: true,
  };
}

function Badge({ status }) {
  const normalizado = String(status || 'DISCONNECTED').toUpperCase();
  return <span className={`badge ${normalizado.toLowerCase()}`}>{statusLabel(normalizado)}</span>;
}

export default function RoboPosVenda() {
  const userLogado = useMemo(() => safeParseUser(), []);
  const empresaIdAtual = useMemo(() => getEmpresaIdFromUser(userLogado), [userLogado]);
  const socketUrl = useMemo(() => getSocketUrl(), []);

  const [canais, setCanais] = useState([]);
  const [canalSelecionadoId, setCanalSelecionadoId] = useState(SELECT_ALL);
  const [logs, setLogs] = useState([]);
  const [loadingCanais, setLoadingCanais] = useState(false);
  const [executando, setExecutando] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [qrModal, setQrModal] = useState(null);
  const [imagemAberta, setImagemAberta] = useState(null);

  const canaisRef = useRef([]);
  const logsKeysRef = useRef(new Set());
  const feedEndRef = useRef(null);

  const canalSelecionado = useMemo(() => {
    if (canalSelecionadoId === SELECT_ALL) return null;
    return canais.find((canal) => getCanalId(canal) === canalSelecionadoId) || null;
  }, [canais, canalSelecionadoId]);

  const canaisConectados = useMemo(() => canais.filter(isCanalConnected), [canais]);
  const stats = useMemo(() => calcularStats(logs), [logs]);

  useEffect(() => {
    canaisRef.current = canais;
  }, [canais]);

  const addFeedback = useCallback((type, message) => {
    setFeedback({ type, message });
    window.clearTimeout(window.__lumixRoboFeedback);
    window.__lumixRoboFeedback = window.setTimeout(() => setFeedback(null), 6000);
  }, []);

  const atualizarCanalRuntime = useCallback((payload = {}) => {
    const canalId = normalizarId(payload.canalWhatsappId || payload.canalId || payload.id);
    if (!canalId) return;

    setCanais((prev) =>
      prev.map((canal) => {
        if (getCanalId(canal) !== canalId) return canal;

        return {
          ...canal,
          status: payload.status || canal.status,
          connected: payload.connected === true || payload.status === 'CONNECTED',
          runtime: {
            ...(canal.runtime || {}),
            connected: payload.connected === true || payload.status === 'CONNECTED',
            status: payload.status || canal.runtime?.status || canal.status,
            qrCode: payload.qrCode || (payload.status === 'QRCODE' ? canal.runtime?.qrCode : null),
            lastStatusAt: payload.timestamp || new Date().toISOString(),
          },
        };
      })
    );
  }, []);

  const fetchCanais = useCallback(async () => {
    setLoadingCanais(true);

    try {
      const { data } = await api.get('/api/whatsapp/canais');
      const lista = resolverArrayApi(data, ['canais', 'channels']).filter((canal) => getCanalId(canal));

      setCanais(lista);

      if (canalSelecionadoId !== SELECT_ALL && !lista.some((canal) => getCanalId(canal) === canalSelecionadoId)) {
        setCanalSelecionadoId(SELECT_ALL);
      }
    } catch (error) {
      addFeedback('error', montarErroApi(error));
    } finally {
      setLoadingCanais(false);
    }
  }, [addFeedback, canalSelecionadoId]);

  const logPertenceAoUsuario = useCallback(
    (data = {}) => {
      const empresaLog = normalizarId(data.empresaId);
      const canalLog = normalizarId(data.canalWhatsappId || data.canalId);
      const unidadeLog = normalizarId(data.unidadeId);

      if (canalLog && canaisRef.current.some((canal) => getCanalId(canal) === canalLog)) return true;
      if (empresaLog && empresaIdAtual && empresaLog === empresaIdAtual) return true;
      if (empresaLog && canaisRef.current.some((canal) => getCanalEmpresaId(canal) === empresaLog || getUnidadeId(canal) === empresaLog)) return true;
      if (unidadeLog && canaisRef.current.some((canal) => getUnidadeId(canal) === unidadeLog)) return true;

      return !empresaLog && !canalLog;
    },
    [empresaIdAtual]
  );

  const logPertenceAoFiltro = useCallback(
    (data = {}) => {
      if (canalSelecionadoId === SELECT_ALL) return true;
      const canalLog = normalizarId(data.canalWhatsappId || data.canalId);
      return canalLog === canalSelecionadoId;
    },
    [canalSelecionadoId]
  );

  const addLog = useCallback(
    (data = {}, source = 'socket') => {
      if (!logPertenceAoUsuario(data) || !logPertenceAoFiltro(data)) return;

      const key = [
        data.empresaId || empresaIdAtual || 'empresa',
        data.canalWhatsappId || data.canalId || 'canal',
        data.tipo || data.type || 'info',
        data.hora || '',
        data.msg || data.message || '',
      ].join('|');

      if (logsKeysRef.current.has(key)) return;
      logsKeysRef.current.add(key);

      if (logsKeysRef.current.size > 600) {
        logsKeysRef.current = new Set(Array.from(logsKeysRef.current).slice(-300));
      }

      const log = {
        tipo: data.tipo || data.type || 'info',
        msg: data.msg || data.message || '',
        hora: data.hora || new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        imagem: data.imagem || data.image || null,
        empresaId: data.empresaId || null,
        unidadeId: data.unidadeId || null,
        canalWhatsappId: data.canalWhatsappId || data.canalId || null,
        source,
      };

      setLogs((prev) => [...prev, log].slice(-250));

      const tipo = tipoLog(log);
      if (tipo === 'finalizado') setExecutando(false);
      if (tipo === 'erro' && String(log.msg).toLowerCase().includes('crítico')) setExecutando(false);
    },
    [empresaIdAtual, logPertenceAoFiltro, logPertenceAoUsuario]
  );

  useEffect(() => {
    fetchCanais();
  }, [fetchCanais]);

  useEffect(() => {
    feedEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [logs]);

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';

    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      auth: token ? { token } : undefined,
      query: empresaIdAtual ? { empresaId: empresaIdAtual } : undefined,
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 800,
    });

    const onConnect = () => setSocketConnected(true);
    const onDisconnect = () => setSocketConnected(false);
    const onLog = (payload) => addLog(payload, 'socket');
    const onStatus = (payload) => atualizarCanalRuntime(payload);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('robo:log', onLog);
    socket.on('whatsapp_status', onStatus);

    if (empresaIdAtual) {
      socket.on(`robo:log:${empresaIdAtual}`, onLog);
      socket.on(`whatsapp_status:${empresaIdAtual}`, onStatus);
      socket.on(`whatsapp_status_${empresaIdAtual}`, onStatus);
    }

    socket.onAny((eventName, payload) => {
      const event = String(eventName || '');
      if (event.startsWith('robo:log')) addLog(payload, 'socket');
      if (event.startsWith('whatsapp_status')) atualizarCanalRuntime(payload);
    });

    return () => {
      socket.disconnect();
    };
  }, [addLog, atualizarCanalRuntime, empresaIdAtual, socketUrl]);

  const conectarCanal = async (canal) => {
    const canalId = getCanalId(canal);
    if (!canalId) return;

    try {
      await api.post(`/api/whatsapp/canais/${canalId}/connect`);
      await fetchCanais();
      addFeedback('success', `Conexão iniciada para ${getCanalNome(canal)}.`);
    } catch (error) {
      addFeedback('error', montarErroApi(error));
    }
  };

  const desconectarCanal = async (canal) => {
    const canalId = getCanalId(canal);
    if (!canalId) return;
    if (!window.confirm(`Desconectar o WhatsApp "${getCanalNome(canal)}"?`)) return;

    try {
      await api.post(`/api/whatsapp/canais/${canalId}/disconnect`);
      await fetchCanais();
      addFeedback('success', `Canal ${getCanalNome(canal)} desconectado.`);
    } catch (error) {
      addFeedback('error', montarErroApi(error));
    }
  };

  const atualizarStatusCanal = async (canal) => {
    const canalId = getCanalId(canal);
    if (!canalId) return;

    try {
      const { data } = await api.get(`/api/whatsapp/canais/${canalId}/status`);
      atualizarCanalRuntime({ ...data, canalWhatsappId: canalId });
    } catch (error) {
      addFeedback('error', montarErroApi(error));
    }
  };

  const iniciarRegua = async () => {
    if (executando) return;

    const canaisValidos = canais.filter((canal) => isMongoId(getCanalId(canal)));
    const canaisAlvo = canalSelecionado ? [canalSelecionado] : canaisValidos.filter(isCanalConnected);

    if (canalSelecionado && !isCanalConnected(canalSelecionado)) {
      addFeedback('error', 'Esse canal está desconectado. Conecte o WhatsApp antes de iniciar a régua.');
      return;
    }

    if (!canaisAlvo.length) {
      addFeedback('error', 'Nenhum canal conectado. Conecte pelo menos um WhatsApp para executar a régua.');
      return;
    }

    const alvoTexto = canalSelecionado
      ? `${getUnidadeNome(canalSelecionado)} · ${getCanalNome(canalSelecionado)}`
      : `${canaisAlvo.length} canal(is) conectado(s)`;

    if (!window.confirm(`Iniciar a régua de pós-venda para ${alvoTexto}?`)) return;

    logsKeysRef.current = new Set();
    setLogs([]);
    setExecutando(true);
    setFeedback(null);

    addLog(
      {
        tipo: 'info',
        msg: `Iniciando varredura da régua | Alvo: ${alvoTexto}`,
        empresaId: empresaIdAtual || null,
        canalWhatsappId: canalSelecionado ? getCanalId(canalSelecionado) : null,
      },
      'local'
    );

    try {
      for (const canal of canaisAlvo) {
        const canalId = getCanalId(canal);
        const payload = montarPayloadReguaCanal(canal, empresaIdAtual);

        addLog(
          {
            tipo: 'analisando',
            msg: `Verificando O.S. da ${getUnidadeNome(canal)} pelo canal ${getCanalNome(canal)}.` ,
            empresaId: payload.empresaId,
            unidadeId: payload.unidadeId,
            canalWhatsappId: canalId,
          },
          'local'
        );

        const response = await api.post(`/api/whatsapp/canais/${canalId}/robo/iniciar`, payload);

        addLog(
          {
            tipo: 'sucesso',
            msg: response?.data?.message || response?.data?.mensagem || `Régua iniciada para ${getCanalNome(canal)}.`,
            empresaId: payload.empresaId,
            unidadeId: payload.unidadeId,
            canalWhatsappId: canalId,
          },
          'api'
        );
      }

      addFeedback('success', `Régua iniciada para ${alvoTexto}.`);
    } catch (error) {
      const msg = montarErroApi(error);
      addFeedback('error', msg);
      addLog({ tipo: 'erro', msg: `Falha ao iniciar a régua: ${msg}` }, 'local');
      setExecutando(false);
    }
  };

  const limparFeed = () => {
    if (executando) return;
    logsKeysRef.current = new Set();
    setLogs([]);
  };

  const renderCanal = (canal) => {
    const canalId = getCanalId(canal);
    const status = getCanalStatus(canal);
    const conectado = isCanalConnected(canal);
    const qr = getQrCode(canal);
    const selecionado = canalSelecionadoId === canalId;

    return (
      <article key={canalId} className={`channel-card ${selecionado ? 'selected' : ''}`}>
        <button type="button" className="channel-main" onClick={() => setCanalSelecionadoId(canalId)}>
          <span className={`status-dot ${conectado ? 'online' : status === 'QRCODE' ? 'qr' : 'offline'}`} />

          <div className="channel-text">
            <strong>{getUnidadeNome(canal)}</strong>
            <small>{getCanalNome(canal)} {canal.numero ? `· ${canal.numero}` : ''}</small>
          </div>

          <Badge status={status} />
        </button>

        {qr && status === 'QRCODE' && (
          <button type="button" className="qr-row" onClick={() => setQrModal(canal)}>
            <Icons.Qr /> Ver QR Code
          </button>
        )}

        <div className="channel-actions">
          <button type="button" onClick={() => atualizarStatusCanal(canal)}>
            <Icons.Refresh /> Status
          </button>

          {conectado ? (
            <button type="button" className="danger" onClick={() => desconectarCanal(canal)}>
              <Icons.Stop /> Desconectar
            </button>
          ) : (
            <button type="button" className="dark" onClick={() => conectarCanal(canal)} disabled={status === 'INITIALIZING'}>
              <Icons.Play /> {status === 'QRCODE' ? 'Aguardando QR' : 'Conectar'}
            </button>
          )}
        </div>
      </article>
    );
  };

  return (
    <div className="robo-page-clean">
      <header className="page-header">
        <div>
          <h1>Régua de relacionamento</h1>
          <p>Verifique as O.S. entregues e execute os envios pelos canais conectados.</p>
        </div>

        <div className="header-status">
          <span className={`status-dot ${socketConnected ? 'online' : 'offline'}`} />
          {socketConnected ? 'Tempo real ativo' : 'Tempo real desconectado'}
        </div>
      </header>

      {feedback && (
        <div className={`feedback ${feedback.type}`}>
          {feedback.type === 'success' ? <Icons.Check /> : <Icons.Alert />}
          <span>{feedback.message}</span>
        </div>
      )}

      <section className="control-panel">
        <label>
          <span>Executar em</span>
          <select value={canalSelecionadoId} onChange={(event) => setCanalSelecionadoId(event.target.value)} disabled={executando}>
            <option value={SELECT_ALL}>Todas as unidades conectadas</option>
            {canais.map((canal) => (
              <option key={getCanalId(canal)} value={getCanalId(canal)}>
                {getUnidadeNome(canal)} · {getCanalNome(canal)}
              </option>
            ))}
          </select>
        </label>

        <button type="button" className="soft-btn" onClick={fetchCanais} disabled={loadingCanais}>
          <Icons.Refresh /> {loadingCanais ? 'Atualizando...' : 'Atualizar canais'}
        </button>

        <button type="button" className="primary-btn" onClick={iniciarRegua} disabled={executando}>
          {executando ? <Icons.Stop /> : <Icons.Play />}
          {executando ? 'Processando...' : 'Iniciar régua'}
        </button>
      </section>

      <section className="metrics-strip">
        <div className="metric-item">
          <span>O.S. verificadas</span>
          <strong>{stats.analisados}</strong>
        </div>
        <div className="metric-item">
          <span>Mensagens enviadas</span>
          <strong>{stats.enviados}</strong>
        </div>
        <div className="metric-item">
          <span>Fora do gatilho</span>
          <strong>{stats.ignorados}</strong>
        </div>
        <div className="metric-item">
          <span>Falhas</span>
          <strong>{stats.erros}</strong>
        </div>
      </section>

      <main className="main-grid">
        <section className="panel">
          <div className="panel-head">
            <div>
              <h2>Canais WhatsApp</h2>
              <p>{canaisConectados.length}/{canais.length} conectados</p>
            </div>
          </div>

          <div className="channels-list">
            <button type="button" className={`all-card ${canalSelecionadoId === SELECT_ALL ? 'selected' : ''}`} onClick={() => setCanalSelecionadoId(SELECT_ALL)}>
              <Icons.Store />
              <div>
                <strong>Todas as unidades</strong>
                <small>Roda apenas nos canais conectados</small>
              </div>
              <b>{canaisConectados.length}</b>
            </button>

            {loadingCanais && canais.length === 0 && <div className="empty">Carregando canais...</div>}
            {!loadingCanais && canais.length === 0 && <div className="empty">Nenhum canal WhatsApp encontrado.</div>}
            {canais.map(renderCanal)}
          </div>
        </section>

        <section className="panel feed-panel">
          <div className="panel-head">
            <div>
              <h2>Resultado da varredura</h2>
              <p>{logs.length ? `${logs.length} evento(s) recebidos` : 'Aguardando execução'}</p>
            </div>

            <button type="button" className="soft-btn compact" onClick={limparFeed} disabled={executando || logs.length === 0}>
              <Icons.Trash /> Limpar
            </button>
          </div>

          <div className="feed-list">
            {logs.length === 0 ? (
              <div className="empty-state">
                <Icons.Phone />
                <strong>Pronto para verificar as O.S.</strong>
                <span>Escolha uma unidade ou execute em todos os canais conectados.</span>
              </div>
            ) : (
              logs.map((log, index) => {
                const tipo = tipoLog(log);
                const canalLog = canais.find((canal) => getCanalId(canal) === normalizarId(log.canalWhatsappId));

                return (
                  <article key={`${log.hora}-${index}`} className={`log-row ${tipo}`}>
                    <span className="log-dot" />
                    <div>
                      <header>
                        <strong>{tipo === 'analisado' ? 'O.S. verificada' : tipo === 'enviado' ? 'Mensagem enviada' : tipo === 'ignorado' ? 'Sem gatilho' : tipo === 'erro' ? 'Falha' : 'Sistema'}</strong>
                        <small>{log.hora}</small>
                      </header>

                      <p>{limparMensagem(log.msg) || '-'}</p>

                      {(canalLog || log.canalWhatsappId || log.unidadeId) && (
                        <footer>
                          {canalLog ? `${getUnidadeNome(canalLog)} · ${getCanalNome(canalLog)}` : `Canal ${String(log.canalWhatsappId || log.unidadeId).slice(-8)}`}
                        </footer>
                      )}

                      {log.imagem && (
                        <button type="button" className="image-link" onClick={() => setImagemAberta(log.imagem)}>
                          Ver imagem anexada
                        </button>
                      )}
                    </div>
                  </article>
                );
              })
            )}

            {executando && (
              <div className="running-line">
                <span />
                Aguardando novos eventos da régua...
              </div>
            )}

            <div ref={feedEndRef} />
          </div>
        </section>
      </main>

      {qrModal && (
        <div className="modal-overlay" onClick={() => setQrModal(null)}>
          <div className="qr-modal" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setQrModal(null)}>
              <Icons.Close />
            </button>

            <span className="eyebrow">Conectar WhatsApp</span>
            <h2>{getCanalNome(qrModal)}</h2>
            <p>{getUnidadeNome(qrModal)}</p>

            {getQrCode(qrModal) ? (
              <img src={normalizarQrCode(getQrCode(qrModal))} alt="QR Code WhatsApp" />
            ) : (
              <div className="empty">QR Code indisponível.</div>
            )}

            <small>Abra o WhatsApp no celular, toque em Aparelhos conectados e escaneie o QR Code.</small>
          </div>
        </div>
      )}

      {imagemAberta && (
        <div className="modal-overlay" onClick={() => setImagemAberta(null)}>
          <div className="image-modal" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setImagemAberta(null)}>
              <Icons.Close />
            </button>
            <img src={imagemAberta} alt="Anexo" />
          </div>
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  :root {
    --bg: #f8fafc;
    --card: #ffffff;
    --text: #111827;
    --muted: #6b7280;
    --border: #e5e7eb;
    --soft: #f3f4f6;
    --blue: #2563eb;
    --green: #16a34a;
    --red: #dc2626;
    --amber: #d97706;
    --radius: 18px;
  }

  * { box-sizing: border-box; }

  .robo-page-clean {
    width: 100%;
    min-height: 100%;
    padding: 18px;
    background: var(--bg);
    color: var(--text);
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  button, select { font: inherit; }

  .page-header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 14px;
  }

  .eyebrow {
    display: inline-flex;
    width: fit-content;
    border: 1px solid #dbeafe;
    background: #eff6ff;
    color: #1d4ed8;
    border-radius: 999px;
    padding: 4px 9px;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: .06em;
    text-transform: uppercase;
  }

  .page-header h1 {
    margin: 9px 0 5px;
    font-size: clamp(24px, 4vw, 34px);
    line-height: 1;
    letter-spacing: -.05em;
    font-weight: 850;
  }

  .page-header p {
    margin: 0;
    max-width: 760px;
    color: var(--muted);
    font-size: 13px;
    line-height: 1.45;
  }

  .header-status {
    min-height: 36px;
    white-space: nowrap;
    border: 1px solid var(--border);
    background: var(--card);
    border-radius: 999px;
    padding: 0 12px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #374151;
    font-size: 12px;
    font-weight: 800;
  }

  .status-dot {
    width: 9px;
    height: 9px;
    border-radius: 999px;
    background: #9ca3af;
    flex: 0 0 auto;
  }

  .status-dot.online { background: var(--green); }
  .status-dot.offline { background: #9ca3af; }
  .status-dot.qr { background: var(--amber); }

  .feedback {
    margin-bottom: 12px;
    border-radius: 14px;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 750;
  }

  .feedback.success { border: 1px solid #bbf7d0; background: #f0fdf4; color: #166534; }
  .feedback.error { border: 1px solid #fecaca; background: #fef2f2; color: #991b1b; }

  .control-panel {
    display: grid;
    grid-template-columns: minmax(260px, 1fr) auto auto;
    gap: 10px;
    align-items: end;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--card);
    padding: 12px;
    margin-bottom: 12px;
  }

  .control-panel label {
    display: grid;
    gap: 6px;
  }

  .control-panel label span {
    color: #374151;
    font-size: 11px;
    font-weight: 850;
  }

  select {
    width: 100%;
    height: 40px;
    border: 1px solid var(--border);
    background: #fff;
    border-radius: 12px;
    padding: 0 11px;
    color: var(--text);
    outline: none;
    font-size: 13px;
    font-weight: 650;
  }

  select:focus {
    border-color: #bfdbfe;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, .10);
  }

  .soft-btn,
  .primary-btn,
  .channel-actions button,
  .all-card,
  .channel-main,
  .qr-row,
  .image-link {
    border: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 999px;
    font-weight: 850;
    transition: .15s ease;
  }

  .soft-btn,
  .primary-btn {
    height: 40px;
    padding: 0 14px;
    white-space: nowrap;
    font-size: 12px;
  }

  .soft-btn {
    border: 1px solid var(--border);
    background: #fff;
    color: #374151;
  }

  .soft-btn.compact { height: 34px; }

  .primary-btn,
  .channel-actions button.dark {
    background: #111827;
    color: #fff;
  }

  .soft-btn:hover,
  .channel-actions button:hover,
  .all-card:hover,
  .channel-main:hover {
    transform: translateY(-1px);
  }

  button:disabled {
    opacity: .55;
    cursor: not-allowed;
    transform: none !important;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 12px;
  }

  .metric-card,
  .panel {
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--card);
  }

  .metric-card {
    padding: 14px;
  }

  .metric-card span {
    display: block;
    color: var(--muted);
    font-size: 11px;
    font-weight: 850;
    text-transform: uppercase;
    letter-spacing: .04em;
  }

  .metric-card strong {
    display: block;
    margin-top: 8px;
    font-size: 28px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: -.04em;
  }

  .main-grid {
    display: grid;
    grid-template-columns: 390px minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }

  .panel {
    overflow: hidden;
  }

  .panel-head {
    min-height: 64px;
    padding: 14px;
    border-bottom: 1px solid var(--border);
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  }

  .panel-head h2 {
    margin: 0;
    font-size: 16px;
    font-weight: 850;
    letter-spacing: -.03em;
  }

  .panel-head p {
    margin: 4px 0 0;
    color: var(--muted);
    font-size: 12px;
  }

  .channels-list {
    padding: 12px;
    display: grid;
    gap: 9px;
    background: #fafafa;
  }

  .all-card,
  .channel-card {
    border: 1px solid var(--border);
    background: #fff;
    border-radius: 15px;
  }

  .all-card {
    width: 100%;
    min-height: 64px;
    padding: 12px;
    justify-content: flex-start;
    text-align: left;
    color: var(--text);
  }

  .all-card.selected,
  .channel-card.selected {
    border-color: #93c5fd;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, .08);
  }

  .all-card div {
    display: grid;
    gap: 2px;
    flex: 1;
  }

  .all-card strong,
  .channel-text strong {
    font-size: 13px;
    font-weight: 850;
  }

  .all-card small,
  .channel-text small {
    color: var(--muted);
    font-size: 11.5px;
  }

  .all-card b {
    min-width: 28px;
    height: 28px;
    border-radius: 999px;
    background: #eff6ff;
    color: #1d4ed8;
    display: grid;
    place-items: center;
    font-size: 12px;
  }

  .channel-card {
    overflow: hidden;
  }

  .channel-main {
    width: 100%;
    min-height: 62px;
    padding: 12px;
    background: transparent;
    color: var(--text);
    justify-content: flex-start;
    text-align: left;
  }

  .channel-text {
    min-width: 0;
    flex: 1;
    display: grid;
    gap: 2px;
  }

  .channel-text strong,
  .channel-text small {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .badge {
    border-radius: 999px;
    padding: 5px 8px;
    font-size: 10.5px;
    font-weight: 850;
    white-space: nowrap;
    color: #6b7280;
    background: #f3f4f6;
  }

  .badge.connected { color: #166534; background: #dcfce7; }
  .badge.qrcode,
  .badge.initializing { color: #92400e; background: #fef3c7; }
  .badge.error { color: #991b1b; background: #fee2e2; }

  .qr-row {
    margin: 0 12px 10px;
    height: 34px;
    padding: 0 11px;
    border: 1px solid #fde68a;
    background: #fffbeb;
    color: #92400e;
    font-size: 12px;
  }

  .channel-actions {
    border-top: 1px solid #f3f4f6;
    padding: 10px 12px 12px;
    display: flex;
    gap: 8px;
  }

  .channel-actions button {
    flex: 1;
    height: 34px;
    border: 1px solid var(--border);
    background: #fff;
    color: #374151;
    font-size: 11.5px;
  }

  .channel-actions button.danger {
    border-color: #fecaca;
    background: #fef2f2;
    color: #991b1b;
  }

  .feed-panel {
    min-height: 600px;
  }

  .feed-list {
    height: 536px;
    overflow-y: auto;
    padding: 12px;
    background: #fafafa;
    display: grid;
    align-content: start;
    gap: 8px;
  }

  .empty,
  .empty-state {
    border: 1px dashed var(--border);
    border-radius: 14px;
    background: #fff;
    color: var(--muted);
    padding: 14px;
    font-size: 12px;
  }

  .empty-state {
    min-height: 220px;
    display: grid;
    place-items: center;
    align-content: center;
    text-align: center;
    gap: 8px;
  }

  .empty-state svg {
    color: #9ca3af;
  }

  .empty-state strong {
    color: var(--text);
    font-size: 14px;
  }

  .log-row {
    display: grid;
    grid-template-columns: 11px minmax(0, 1fr);
    gap: 10px;
    border: 1px solid var(--border);
    background: #fff;
    border-radius: 14px;
    padding: 11px;
  }

  .log-dot {
    margin-top: 5px;
    width: 9px;
    height: 9px;
    border-radius: 999px;
    background: #9ca3af;
  }

  .log-row.analisado .log-dot { background: var(--blue); }
  .log-row.enviado .log-dot { background: var(--green); }
  .log-row.ignorado .log-dot { background: var(--amber); }
  .log-row.erro .log-dot { background: var(--red); }

  .log-row header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .log-row header strong {
    font-size: 12px;
    font-weight: 850;
  }

  .log-row header small {
    color: var(--muted);
    font-size: 11px;
    white-space: nowrap;
  }

  .log-row p {
    margin: 5px 0 0;
    color: #374151;
    font-size: 12.5px;
    line-height: 1.45;
  }

  .log-row footer {
    margin-top: 7px;
    color: var(--muted);
    font-size: 11px;
  }

  .image-link {
    margin-top: 8px;
    padding: 0;
    background: transparent;
    color: #1d4ed8;
    font-size: 12px;
    justify-content: flex-start;
  }

  .running-line {
    border: 1px solid #dbeafe;
    background: #eff6ff;
    color: #1d4ed8;
    border-radius: 14px;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 800;
  }

  .running-line span {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: #2563eb;
    animation: pulse 1s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: .35; }
    50% { opacity: 1; }
  }

  .modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(15, 23, 42, .58);
    display: grid;
    place-items: center;
    padding: 18px;
  }

  .qr-modal,
  .image-modal {
    position: relative;
    width: min(420px, 100%);
    border-radius: 22px;
    background: #fff;
    padding: 20px;
    text-align: center;
    box-shadow: 0 24px 80px rgba(15, 23, 42, .24);
  }

  .qr-modal h2 {
    margin: 12px 0 4px;
    font-size: 20px;
    letter-spacing: -.04em;
  }

  .qr-modal p {
    margin: 0 0 14px;
    color: var(--muted);
    font-size: 13px;
  }

  .qr-modal img {
    width: 260px;
    max-width: 100%;
    border-radius: 16px;
    border: 1px solid var(--border);
    padding: 10px;
    background: #fff;
  }

  .qr-modal small {
    display: block;
    margin-top: 12px;
    color: var(--muted);
    font-size: 12px;
    line-height: 1.4;
  }

  .modal-close {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 34px;
    height: 34px;
    border: 0;
    border-radius: 999px;
    background: #f3f4f6;
    color: #374151;
    display: grid;
    place-items: center;
    cursor: pointer;
  }

  .image-modal {
    width: min(860px, 100%);
  }

  .image-modal img {
    display: block;
    width: 100%;
    max-height: 78vh;
    object-fit: contain;
    border-radius: 16px;
  }

  @media (max-width: 1080px) {
    .main-grid {
      grid-template-columns: 1fr;
    }

    .feed-list {
      height: 480px;
    }
  }

  @media (max-width: 760px) {
    .robo-page-clean {
      padding: 10px;
      background: #fff;
    }

    .page-header,
    .control-panel,
    .metrics-grid {
      grid-template-columns: 1fr;
      display: grid;
    }

    .header-status,
    .soft-btn,
    .primary-btn {
      width: 100%;
    }

    .metric-card strong {
      font-size: 24px;
    }

    .channel-actions {
      display: grid;
    }

    .feed-list {
      height: auto;
      max-height: 72vh;
    }
  }

  /* =========================================================================
     V2 — PADRÃO GLOBAL DA PLATAFORMA
     Interface branca, plana, sem aplicativo dentro do aplicativo.
  ========================================================================= */

  :root {
    --bg: #ffffff;
    --card: #ffffff;
    --text: #171717;
    --muted: #6f6f6f;
    --border: #e5e5e5;
    --soft: #f6f6f6;
    --blue: #111111;
    --green: #16803c;
    --red: #c9362b;
    --amber: #b26b00;
    --radius: 10px;
  }

  .robo-page-clean {
    width: 100%;
    min-height: 100%;
    padding: 18px 24px 34px !important;
    background: #ffffff !important;
    color: #171717 !important;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Arial, sans-serif !important;
    -webkit-font-smoothing: antialiased;
    text-rendering: geometricPrecision;
  }

  .page-header {
    min-height: 48px;
    margin: 0 0 16px !important;
    padding: 0 0 14px;
    border-bottom: 1px solid #ececec;
    align-items: center !important;
  }

  .eyebrow {
    display: none !important;
  }

  .page-header h1 {
    margin: 0 0 3px !important;
    color: #171717;
    font-size: 18px !important;
    line-height: 1.2 !important;
    letter-spacing: -.025em !important;
    font-weight: 700 !important;
  }

  .page-header p {
    max-width: none !important;
    color: #747474 !important;
    font-size: 11.5px !important;
    line-height: 1.4 !important;
  }

  .header-status {
    min-height: 32px !important;
    padding: 0 9px !important;
    border: 1px solid #dedede !important;
    border-radius: 7px !important;
    color: #4d4d4d !important;
    background: #ffffff !important;
    font-size: 10.5px !important;
    font-weight: 650 !important;
    box-shadow: none !important;
  }

  .feedback {
    margin-bottom: 12px !important;
    border-radius: 8px !important;
    padding: 9px 10px !important;
    font-size: 11.5px !important;
    font-weight: 650 !important;
    box-shadow: none !important;
  }

  .control-panel {
    grid-template-columns: minmax(240px, 1fr) auto auto !important;
    gap: 8px !important;
    margin-bottom: 12px !important;
    padding: 0 0 14px !important;
    border: 0 !important;
    border-bottom: 1px solid #ececec !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .control-panel label {
    gap: 4px !important;
  }

  .control-panel label span {
    color: #686868 !important;
    font-size: 10px !important;
    font-weight: 700 !important;
    letter-spacing: .04em;
    text-transform: uppercase;
  }

  select {
    height: 34px !important;
    padding: 0 9px !important;
    border: 1px solid #d8d8d8 !important;
    border-radius: 7px !important;
    color: #171717 !important;
    background: #ffffff !important;
    font-size: 11.5px !important;
    font-weight: 600 !important;
    box-shadow: none !important;
  }

  select:focus {
    border-color: #111111 !important;
    box-shadow: 0 0 0 2px rgba(0,0,0,.05) !important;
  }

  .soft-btn,
  .primary-btn,
  .channel-actions button,
  .all-card,
  .channel-main,
  .qr-row,
  .image-link {
    border-radius: 7px !important;
    font-weight: 650 !important;
    transition: none !important;
    transform: none !important;
    box-shadow: none !important;
  }

  .soft-btn,
  .primary-btn {
    height: 34px !important;
    padding: 0 11px !important;
    font-size: 11px !important;
  }

  .soft-btn {
    border: 1px solid #d8d8d8 !important;
    color: #333333 !important;
    background: #ffffff !important;
  }

  .primary-btn,
  .channel-actions button.dark {
    border: 1px solid #111111 !important;
    color: #ffffff !important;
    background: #111111 !important;
  }

  .soft-btn:hover,
  .channel-actions button:hover,
  .all-card:hover,
  .channel-main:hover {
    transform: none !important;
  }

  .metrics-strip {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0;
    margin-bottom: 14px;
    border-top: 1px solid #ececec;
    border-bottom: 1px solid #ececec;
    background: #ffffff;
  }

  .metric-item {
    min-height: 62px;
    padding: 11px 14px;
    border-right: 1px solid #ececec;
    background: #ffffff;
  }

  .metric-item:last-child {
    border-right: 0;
  }

  .metric-item span {
    display: block;
    color: #777777;
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
  }

  .metric-item strong {
    display: block;
    margin-top: 5px;
    color: #171717;
    font-size: 22px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: -.03em;
  }

  .main-grid {
    grid-template-columns: 320px minmax(0, 1fr) !important;
    gap: 0 !important;
    border-top: 1px solid #e8e8e8;
    border-bottom: 1px solid #e8e8e8;
    background: #ffffff;
  }

  .panel {
    border: 0 !important;
    border-radius: 0 !important;
    background: #ffffff !important;
    box-shadow: none !important;
  }

  .panel:first-child {
    border-right: 1px solid #e8e8e8 !important;
  }

  .panel-head {
    min-height: 50px !important;
    padding: 10px 12px !important;
    border-bottom: 1px solid #ececec !important;
    align-items: center !important;
  }

  .panel-head h2 {
    font-size: 13px !important;
    font-weight: 700 !important;
    letter-spacing: -.015em !important;
  }

  .panel-head p {
    margin-top: 2px !important;
    color: #777777 !important;
    font-size: 10.5px !important;
  }

  .channels-list {
    padding: 0 !important;
    gap: 0 !important;
    background: #ffffff !important;
  }

  .all-card,
  .channel-card {
    border: 0 !important;
    border-bottom: 1px solid #eeeeee !important;
    border-radius: 0 !important;
    background: #ffffff !important;
    box-shadow: none !important;
  }

  .all-card {
    min-height: 54px !important;
    padding: 9px 11px !important;
    color: #171717 !important;
  }

  .all-card.selected,
  .channel-card.selected {
    border-color: #eeeeee !important;
    background: #f7f7f7 !important;
    box-shadow: inset 2px 0 0 #111111 !important;
  }

  .all-card b {
    min-width: 24px !important;
    height: 24px !important;
    border-radius: 6px !important;
    color: #171717 !important;
    background: #ededed !important;
    font-size: 10.5px !important;
  }

  .channel-main {
    min-height: 54px !important;
    padding: 9px 11px !important;
    color: #171717 !important;
    background: transparent !important;
  }

  .all-card strong,
  .channel-text strong {
    font-size: 11.5px !important;
    font-weight: 700 !important;
  }

  .all-card small,
  .channel-text small {
    color: #777777 !important;
    font-size: 10px !important;
  }

  .badge {
    padding: 4px 6px !important;
    border-radius: 6px !important;
    font-size: 9px !important;
    font-weight: 700 !important;
  }

  .qr-row {
    margin: 0 10px 8px !important;
    height: 30px !important;
    font-size: 10.5px !important;
  }

  .channel-actions {
    padding: 8px 10px 10px !important;
    border-top: 1px solid #eeeeee !important;
    gap: 6px !important;
  }

  .channel-actions button {
    height: 30px !important;
    border-radius: 6px !important;
    font-size: 10px !important;
  }

  .feed-panel {
    min-height: 540px !important;
  }

  .feed-list {
    height: 490px !important;
    padding: 0 !important;
    gap: 0 !important;
    background: #ffffff !important;
  }

  .empty,
  .empty-state {
    border: 0 !important;
    border-radius: 0 !important;
    color: #777777 !important;
    background: #ffffff !important;
    font-size: 11px !important;
  }

  .empty-state {
    min-height: 220px !important;
  }

  .log-row {
    grid-template-columns: 9px minmax(0, 1fr) !important;
    gap: 9px !important;
    padding: 10px 12px !important;
    border: 0 !important;
    border-bottom: 1px solid #eeeeee !important;
    border-radius: 0 !important;
    background: #ffffff !important;
    box-shadow: none !important;
  }

  .log-row header strong {
    font-size: 11px !important;
    font-weight: 700 !important;
  }

  .log-row header small,
  .log-row footer {
    color: #7a7a7a !important;
    font-size: 9.5px !important;
  }

  .log-row p {
    margin-top: 3px !important;
    color: #444444 !important;
    font-size: 11px !important;
    line-height: 1.4 !important;
  }

  .image-link {
    color: #171717 !important;
    font-size: 10.5px !important;
    text-decoration: underline;
  }

  .running-line {
    margin: 10px 12px;
    border: 1px solid #dddddd !important;
    border-radius: 7px !important;
    color: #333333 !important;
    background: #f7f7f7 !important;
    font-size: 10.5px !important;
    font-weight: 650 !important;
  }

  .modal-overlay {
    z-index: 20000 !important;
    background: rgba(0,0,0,.32) !important;
    backdrop-filter: none !important;
  }

  .qr-modal,
  .image-modal {
    border: 1px solid #dddddd !important;
    border-radius: 12px !important;
    box-shadow: 0 18px 60px rgba(0,0,0,.18) !important;
  }

  .modal-close {
    border: 1px solid #dddddd !important;
    border-radius: 7px !important;
    background: #ffffff !important;
    box-shadow: none !important;
  }

  @media (max-width: 1080px) {
    .main-grid {
      grid-template-columns: 1fr !important;
    }

    .panel:first-child {
      border-right: 0 !important;
      border-bottom: 1px solid #e8e8e8 !important;
    }

    .feed-list {
      height: 460px !important;
    }
  }

  @media (max-width: 760px) {
    .robo-page-clean {
      padding: 14px 12px 24px !important;
    }

    .page-header,
    .control-panel,
    .metrics-strip {
      grid-template-columns: 1fr !important;
      display: grid !important;
    }

    .metrics-strip {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }

    .metric-item:nth-child(2) {
      border-right: 0;
    }

    .metric-item:nth-child(-n+2) {
      border-bottom: 1px solid #ececec;
    }

    .header-status,
    .soft-btn,
    .primary-btn {
      width: 100%;
    }

    .feed-list {
      height: auto !important;
      max-height: 72vh !important;
    }
  }

`;
